/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { NpvrRecordingCapabilities } from 'client-code/channels-service';
import { OfferPreview, RecordingAvailability } from 'client-code/previews';

import { MILLISECONDS_IN_A_SECOND } from '../../config';
import { Channel } from '../entities';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { TProgramCell } from '../types';
import { DateTime } from '../utils';
import { VideoPlaybackController } from '../videoplayer/VideoPlaybackController';

type TRecordingRestriction = {
    isRestricted: boolean;
    message: string;
};

export class RecordingRestriction {
    private static readonly TAG: string = 'RecordingRestriction';

    private static readonly DEFAULT_RR_DETAILS: TRecordingRestriction = {
        isRestricted: false,
        message: '',
    };

    public static readonly ACTION_STRIP: string = 'action-strip-restriction';

    public static readonly ACTION_STRIP_KEY_OK: string = 'action-strip-key-ok-restriction';

    public static readonly CHANNEL_CELL: string = 'channel-cell-restriction';

    public static readonly MESSAGE_SEPARATOR: string = '|';

    public static readonly OVERLAY_DIALOG: string = 'overlay-dialog-restriction';

    public static readonly PREVIEW_AREA: string = 'preview-area-restriction';

    public static readonly PROGRAM_CELL: string = 'program-cell-restriction';

    /* eslint-disable function-paren-newline */
    private static isChannelRestricted(channel: Channel | undefined): boolean {
        if (channel) {
            const { policyInfo } = channel;
            const isRestricted: boolean =
                !policyInfo?.some((data) =>
                    data?.recordingRules?.restrictedCapability?.includes(
                        NpvrRecordingCapabilities.AllowRecording,
                    ),
                ) || false;
            return isRestricted;
        }

        return false;
    }

    private static isChannelSubscribed(channel: Channel | undefined): boolean {
        return !!channel?.isSubscribed;
    }

    private static isOfferRestricted(preview: OfferPreview | undefined) {
        if (!preview) return false;

        const { recordingAvailability } = preview;
        return !(
            recordingAvailability === undefined ||
            recordingAvailability === RecordingAvailability.RecordingAllowed ||
            recordingAvailability === RecordingAvailability.RecordingAllowedAsNoChannelData
        );
    }

    private static isShowRestricted(
        channel: Channel | undefined,
        programCell: TProgramCell,
    ): boolean {
        const { startTime, duration, isRecordable } = programCell;
        if (isRecordable !== undefined && isRecordable === false) return true;
        let calcStartTime: number = 0;
        try {
            calcStartTime = startTime.getTime();
        } catch (error) {
            calcStartTime = new DateTime(startTime as Date).getTime();
            Log.i(RecordingRestriction.TAG, `${error}`);
        }
        const calcEndTime = calcStartTime + duration * MILLISECONDS_IN_A_SECOND;
        const calcCurrentTime = Date.now();
        if (calcEndTime >= calcCurrentTime) return false;

        const RITPPolicy = channel?.policyInfo?.find(
            (data) => data?.recordingRules?.recordInThePastSeconds,
        );
        const recordInThePastSeconds = RITPPolicy?.recordingRules?.recordInThePastSeconds ?? 0;
        if (recordInThePastSeconds !== 0) {
            const recordAvailability =
                calcCurrentTime - recordInThePastSeconds * MILLISECONDS_IN_A_SECOND;
            if (calcEndTime <= recordAvailability) {
                return true;
            }
            return false;
        }

        return true;
    }

    private static evalActionStripRestriction(
        channel: Channel | undefined,
        preview: OfferPreview | undefined,
    ): TRecordingRestriction {
        if (RecordingRestriction.isChannelRestricted(channel)) {
            return {
                isRestricted: true,
                message: `${
                    Locale.getString(LocaleKeys.RR_RECORDING_NOT_PERMITTED_TEXT) +
                    RecordingRestriction.MESSAGE_SEPARATOR +
                    Locale.getString(LocaleKeys.RR_CHANNEL_RESTRICTION_MESSAGE)
                }`,
            };
        }
        if (RecordingRestriction.isOfferRestricted(preview)) {
            return {
                isRestricted: true,
                message: `${
                    Locale.getString(LocaleKeys.RR_RECORDING_NOT_PERMITTED_TEXT) +
                    RecordingRestriction.MESSAGE_SEPARATOR +
                    Locale.getString(LocaleKeys.RR_COPYRIGHT_RESTRICTION_MESSAGE)
                }`,
            };
        }
        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    private static evalChannelCellRestriction(channel: Channel | undefined): TRecordingRestriction {
        if (RecordingRestriction.isChannelRestricted(channel)) {
            return { isRestricted: true, message: '' };
        }
        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    private static evalOverlayDialogRestriction(
        channel: Channel | undefined,
        programCell: TProgramCell | undefined,
    ): TRecordingRestriction {
        if (RecordingRestriction.isChannelRestricted(channel)) {
            return {
                isRestricted: true,
                message: Locale.getString(LocaleKeys.RR_CHANNEL_RESTRICTION_MESSAGE),
            };
        }

        if (programCell && RecordingRestriction.isShowRestricted(channel, programCell)) {
            return {
                isRestricted: true,
                message: Locale.getString(LocaleKeys.RR_COPYRIGHT_RESTRICTION_MESSAGE),
            };
        }

        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    private static evalPreviewAreaRestriction(
        channel: Channel | undefined,
        preview: OfferPreview | undefined,
    ): TRecordingRestriction {
        if (RecordingRestriction.isChannelRestricted(channel))
            return {
                isRestricted: true,
                message: Locale.getString(LocaleKeys.RR_CHANNEL_RESTRICTION_MESSAGE),
            };

        if (RecordingRestriction.isOfferRestricted(preview))
            return {
                isRestricted: true,
                message: Locale.getString(LocaleKeys.RR_COPYRIGHT_RESTRICTION_MESSAGE),
            };

        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    private static evalProgramCellRestriction(
        channel: Channel | undefined,
        programCell: TProgramCell | undefined,
    ): TRecordingRestriction {
        if (!RecordingRestriction.isChannelRestricted(channel) && programCell) {
            return {
                isRestricted: RecordingRestriction.isShowRestricted(channel, programCell),
                message: '',
            };
        }

        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    /**
     * Returns restriction details object based on displayType and provided params.
     * @static
     * @param {string} displayType
     * @param {Channel} [channel]
     * @param {TProgramCell} [programCell]
     * @param {OfferPreview} [preview]
     * @return {*}  {TRecordingRestriction}
     * @memberof RecordingRestriction
     */
    public static getRestrictionDetails(
        displayType: string,
        channel?: Channel,
        programCell?: TProgramCell,
        preview?: OfferPreview,
    ): TRecordingRestriction {
        let restrictionDetails: TRecordingRestriction = RecordingRestriction.DEFAULT_RR_DETAILS;
        if (channel && !RecordingRestriction.isChannelSubscribed(channel))
            return restrictionDetails;

        switch (displayType) {
            case RecordingRestriction.ACTION_STRIP:
                restrictionDetails = RecordingRestriction.evalActionStripRestriction(
                    channel,
                    preview,
                );
                break;

            case RecordingRestriction.ACTION_STRIP_KEY_OK:
                restrictionDetails = {
                    isRestricted: true,
                    message: Locale.getString(
                        RecordingRestriction.isChannelRestricted(
                            VideoPlaybackController.getInstance().getPlayable()?.channel,
                        )
                            ? LocaleKeys.RR_LIVE_TV_CHANNEL_NOTIFICATION_MESSAGE
                            : LocaleKeys.RR_LIVE_TV_NOTIFICATION_MESSAGE,
                    ),
                };
                break;

            case RecordingRestriction.CHANNEL_CELL:
                restrictionDetails = RecordingRestriction.evalChannelCellRestriction(channel);
                break;

            case RecordingRestriction.OVERLAY_DIALOG:
                restrictionDetails = RecordingRestriction.evalOverlayDialogRestriction(
                    channel,
                    programCell,
                );
                break;

            case RecordingRestriction.PREVIEW_AREA:
                restrictionDetails = RecordingRestriction.evalPreviewAreaRestriction(
                    channel,
                    preview,
                );
                break;

            case RecordingRestriction.PROGRAM_CELL:
                restrictionDetails = RecordingRestriction.evalProgramCellRestriction(
                    channel,
                    programCell,
                );
                break;

            default:
        }
        return restrictionDetails;
    }
}
